import type { ApiClient } from './client'
import type { components } from '@aurigaengineering/contracts-admin'

export type LlmSetup = components['schemas']['LlmSetupView']
export type FoundryAccess = components['schemas']['FoundryAccessView']
export type LlmProvider = components['schemas']['LlmProviderView']
export type LlmModel = components['schemas']['LlmModelView']

/**
 * What LLM Settings does, as six calls.
 *
 * The screen talks to this rather than to the typed client, for one reason: a test can hand it
 * a stand-in. Everything below is the contract's own shape -- these are the generated types,
 * not a second set written by hand -- so what a test exercises is the screen's behaviour and
 * not a fiction about the wire.
 */
export interface LlmApi {
  read(): Promise<LlmSetup>
  /** Whether the API can be issued a Foundry token as itself. Its own call: it talks to Azure. */
  access(): Promise<FoundryAccess>
  saveProvider(code: string, name: string, endpoint: string | null): Promise<void>
  addModel(code: string, name: string, version: string): Promise<void>
  describeModel(id: string, name: string, version: string, enabled: boolean): Promise<void>
  removeModel(id: string): Promise<void>
}

/**
 * The real one, over the typed client.
 *
 * Every call throws on a refusal, carrying the API's own problem detail where there is one. A
 * settings screen that swallowed a 403 would show an Owner a change that never landed.
 */
export function llmApi(client: ApiClient): LlmApi {
  return {
    async read() {
      const { data, error } = await client.GET('/admin/llm')
      return data ?? failed(error)
    },

    async access() {
      const { data, error } = await client.GET('/admin/llm/access')
      return data ?? failed(error)
    },

    async saveProvider(code, name, endpoint) {
      const { error } = await client.PUT('/admin/llm/providers/{code}', {
        params: { path: { code } },
        body: { name, endpoint },
      })
      if (error) failed(error)
    },

    async addModel(code, name, version) {
      const { error } = await client.POST('/admin/llm/providers/{code}/models', {
        params: { path: { code } },
        body: { name, version },
      })
      if (error) failed(error)
    },

    async describeModel(id, name, version, enabled) {
      const { error } = await client.PUT('/admin/llm/models/{id}', {
        params: { path: { id } },
        body: { name, version, enabled },
      })
      if (error) failed(error)
    },

    async removeModel(id) {
      const { error } = await client.DELETE('/admin/llm/models/{id}', {
        params: { path: { id } },
      })
      if (error) failed(error)
    },
  }
}

/**
 * Turns whatever the API refused with into an Error the screen can show.
 *
 * ProblemDetails where the API sent one -- its `detail` is written to be read by the person
 * who caused it -- and the status alone where it did not, which is what a 401 or a 403 from
 * the pipeline looks like.
 */
function failed(error: unknown): never {
  const problem = error as { detail?: string; title?: string } | undefined

  throw new Error(problem?.detail ?? problem?.title ?? 'The API refused that.')
}
